'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CART_CLEAR_CONFIRMATION_COPY } from './cart-clear-confirmation.const';

import { Button } from '@/components/ui/button/button.component';
import { Modal } from '@/components/ui/modal.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { CartClearReason } from '@/types/cart-clear-confirmation.types';

export interface CartClearConfirmationModalProps {
  open: boolean;
  reason: CartClearReason | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CartClearConfirmationModal({
  open,
  reason,
  onConfirm,
  onCancel,
}: CartClearConfirmationModalProps) {
  if (!reason) {
    return null;
  }

  const { title, description, confirmLabel, cancelLabel } = CART_CLEAR_CONFIRMATION_COPY[reason];

  return (
    <Modal open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <Modal.Content
        title={title}
        className="md:max-w-[25rem]"
        data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_MODAL}
        slotHeader={(
          <header className="flex w-full items-start justify-between gap-2 pt-5 pr-5 pb-4 pl-6">
            <h2
              className="text-2xl leading-8 font-bold text-gray-900"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_TITLE}
            >
              {title}
            </h2>

            <Modal.Close
              asChild
              className="shrink-0"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CLOSE_BUTTON}
            >
              <ModalCloseButton />
            </Modal.Close>
          </header>
        )}
      >
        <div className="flex flex-col gap-6 px-6 pb-5">
          <Typography
            variant={TypographyVariant.BODY_TEXT}
            data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_DESCRIPTION}
          >
            {description}
          </Typography>

          <div className="flex flex-col gap-4">
            <Button
              color="danger"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CONFIRM_BUTTON}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>

            <Button
              variant="secondary"
              color="primary"
              className="leading-6"
              data-testid={DataTestIdEnum.CART_CLEAR_CONFIRMATION_CANCEL_BUTTON}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}
